import type { ResourceAttribution } from "@/types_api/backend/transport/get_metric_response"
import type { Annotation } from "@/types/api/Annotation"
import { MetricErrorStatus } from "@/types/api/MetricOutput"
import type { MetricThresholdResource } from "@/types/api/MetricThreshold"
import type { JsonObject } from "@/types/json"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { MetricMetadata } from "@/types/metrics/metadata"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { supportsTranscriptFilter } from "../utils/metricRecordCapabilities"
import {
  getHighlightedMessageIndexes,
  getMetricOutputVersionStatus,
} from "../utils/metricRecordOutputBehavior"

export class MetricRecordCore implements MetricRecordData {
  attribution?: ResourceAttribution | null
  id: string
  metric_name!: string
  metric_display_name!: string
  metric_metadata!: MetricMetadata
  description!: string
  metric_manager!: string
  locked!: boolean
  category?: string
  overviewSize?: "small" | "medium" | "large"
  created_at?: string
  organization_id?: string
  organization_slug?: string
  organization_display_name?: string
  documentation?: string
  created_by?: string
  last_updated_at?: string
  last_updated_by?: string
  active?: boolean
  output_type?: string
  tags?: string[]
  organization_threshold?: MetricThresholdResource | null
  current_version_id?: string | null
  current_version_number?: number | null
  hasComparableVersionLineage = true
  dependency_metric_ids?: string[] | null
  unit: string
  aggregate_units: string

  constructor(data: MetricRecordData) {
    Object.assign(this, data)
    this.id = data.id ?? ""
    this.documentation = this.description
    this.unit = ""
    this.aggregate_units = ""
  }

  /** Whether a completed output has a usable value for its metric-specific UI. */
  hasValidOutputValue(_mo: RunMetricOutput): boolean {
    return true
  }

  hasValidDeepDiveData(mo: RunMetricOutput): boolean {
    return (
      mo.error_status === MetricErrorStatus.COMPLETED &&
      this._hasValidDeepDiveData(mo)
    )
  }

  /** Some metrics retain measured details when their summary value is unavailable. */
  protected _hasValidDeepDiveData(mo: RunMetricOutput): boolean {
    return this.hasValidOutputValue(mo)
  }

  supportsTranscriptFilter(): boolean {
    return supportsTranscriptFilter(this)
  }

  compareValues(
    _value1: {
      float_value?: number | null
      string_value?: string | null
      set_value?: string[] | null
    },
    _value2: {
      float_value?: number | null
      string_value?: string | null
      set_value?: string[] | null
    },
  ): boolean {
    throw new Error(
      `compareValues not implemented for metric_manager=${this.metric_manager}`,
    )
  }

  annotationMatchesMetricOutput(
    annotation: Annotation,
    metricOutput: {
      output_type: string
      float_value: number | null
      string_value: string | null
      set_value: string[] | null
      result_json?: JsonObject | null
    },
  ): boolean {
    return this.compareValues(
      {
        float_value: annotation.ground_truth_float_value,
        string_value: annotation.ground_truth_string_value,
        set_value: annotation.ground_truth_set_value,
      },
      metricOutput,
    )
  }

  get hasVersionedConfig(): boolean {
    return false
  }

  isMetricOutputCurrentVersion(_metricOutput: {
    result_json?: JsonObject | null
  }): boolean {
    return true
  }

  getVersionDiff(_metricOutput: {
    result_json?: JsonObject | null
  }): Array<{ field: string; current: string; previous: string }> {
    return []
  }

  getOutputVersionStatus(metricOutput: {
    metric_version_number?: number | null
  }): { ranVersion: number; currentVersion: number; isStale: boolean } | null {
    if (!this.hasComparableVersionLineage) {
      return null
    }
    return getMetricOutputVersionStatus(
      metricOutput,
      this.current_version_number,
    )
  }

  isMetricOutputStale(metricOutput: {
    metric_version_number?: number | null
    result_json?: JsonObject | null
  }): boolean {
    return this.getOutputVersionStatus(metricOutput)?.isStale ?? false
  }

  preprocessMetadataBeforeSave(): MetricRecordCore {
    return this
  }

  getHighlightedMessages(metricOutput: RunMetricOutput): number[] {
    return getHighlightedMessageIndexes(metricOutput)
  }
}
